import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ModuleWithProviders, NgModule, isDevMode } from '@angular/core';
import { TranslocoModule, provideTransloco } from '@jsverse/transloco';

import { LanguageCode, LanguageDefinition } from '@shure/shared/models';

import { AppLanguageListService } from './app-language-list.service';
import { TRANSLATION_HTTP_REQUEST_BUILD_IDENTIFIER, TranslocoHttpLoader } from './transloco-http.loader';

@NgModule({
	exports: [TranslocoModule],
	imports: [TranslocoModule],
	providers: [provideHttpClient(withInterceptorsFromDi())]
})
export class TranslocoRootModule {
	/**
	 * Setup application configuration for transloco.
	 *
	 * @param languages List of supported languages in the application, with their code and display name.
	 * @param flatten Optional boolean to control whether to flatten translation files.
	 *                Defaults to true in production and false in development.
	 * @param buildIdentifier Optional build identifier string. Appended to translation file URLs as query parameter for cache busting.
	 *                        If not provided, no cache busting parameter will be added.
	 *                        Generally web/cloud apps should provide this (e.g. their version string) while Electron apps should omit it.
	 */
	public static forRoot(
		languages: LanguageDefinition[],
		flatten?: boolean,
		buildIdentifier?: string
	): ModuleWithProviders<TranslocoRootModule> {
		// Default language fallback
		let defaultLanguage = LanguageCode.English;
		if (!languages.some((l) => l.id === defaultLanguage)) {
			defaultLanguage = languages[0].id;
		}

		const appLanguageListService = new AppLanguageListService(languages);

		// Create Transloco module
		return {
			ngModule: TranslocoRootModule,
			providers: [
				provideTransloco({
					config: {
						defaultLang: defaultLanguage,
						fallbackLang: defaultLanguage,
						availableLangs: languages.map((l) => ({ id: l.id, label: l.name })),
						prodMode: !isDevMode(),
						reRenderOnLangChange: true,
						flatten: { aot: flatten !== undefined ? flatten : !isDevMode() },
						missingHandler: {
							logMissingKey: true,
							allowEmpty: false,
							useFallbackTranslation: true
						}
					},
					loader: TranslocoHttpLoader
				}),
				{
					provide: AppLanguageListService,
					useValue: appLanguageListService
				},
				{
					provide: TRANSLATION_HTTP_REQUEST_BUILD_IDENTIFIER,
					useValue: buildIdentifier
				}
			]
		};
	}
}
